import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../../../../../../shared/Modal/Modal.js";
import { Button } from "../../../../../../shared/Button/Button.js";
import { Checkbox } from "../../../../../../shared/Checkbox/Checkbox.js";
import { CircularProgress } from "../../../../../../shared/CircularProgress/CircularProgress.js";
import { InfoIcon } from "../../../../../../assets/Icons/InfoIcon.js";
import { AngleDownIcon } from "../../../../../../assets/Icons/AngleDownIcon.js";
import { SurchargeAgreementArticle } from "./SurchargeAgreementArticle.js";
import { useSurchargeAgreement } from "./hooks/useSurchargeAgreement.js";
import { useAcceptSurchargeAgreement } from "./hooks/useAcceptSurchargeAgreement.js";
import { useDownloadSurchargeAgreement } from "./hooks/useDownloadSurchargeAgreement.js";
import styles from "./scss/SurchargeAgreementModal.module.scss";

// Scroll-to-bottom is checked with a small tolerance since sub-pixel layout rounding can leave
// scrollHeight - scrollTop - clientHeight at 0.x instead of exactly 0.
const SCROLL_BOTTOM_TOLERANCE = 8;

/**
 * Surcharge enrollment/agreement modal, used two ways:
 *  - "Gate" mode (onContinue passed): shown before creating/updating a surcharge. Requires
 *    scrolling through the agreement, checking acceptance (persisted per clinic via the accept
 *    endpoint), then Continue proceeds with the pending surcharge save. If the clinic already
 *    accepted, it skips straight to onContinue.
 *  - "View" mode (no onContinue): opened from the "Surcharge Agreement" button under the clinic
 *    taxes listing. Read-only — shows who accepted and when, plus the agreement text and a
 *    download link.
 *
 * clinicId is undefined during clinic creation (the clinic doesn't exist yet, so there's nothing
 * to persist acceptance against yet) — in that case the gate is local: scroll + check unlocks
 * Continue, and the checked state is lifted to the caller via localAccepted/onAcceptLocal so it
 * can be sent along with the create-clinic request. The backend persists the real acceptance
 * (with the real clinic_id) as part of that same request, once the clinic actually exists.
 */
const SurchargeAgreementModal = ({
  clinicId,
  onContinue,
  onBack,
  localAccepted,
  onAcceptLocal,
}) => {
  const isGateMode = Boolean(onContinue);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(Boolean(localAccepted));
  const hasAutoContinued = useRef(false);
  const scrollAreaRef = useRef(null);

  const { data: agreement, isLoading: isLoadingAgreement } =
    useSurchargeAgreement(clinicId);
  const { mutate: acceptAgreement, isLoading: isAccepting } =
    useAcceptSurchargeAgreement(clinicId);
  const { mutate: downloadAgreement, isLoading: isDownloading } =
    useDownloadSurchargeAgreement(clinicId);

  const alreadyAccepted = Boolean(agreement?.accepted);

  useEffect(() => {
    if (
      isGateMode &&
      clinicId &&
      alreadyAccepted &&
      !hasAutoContinued.current
    ) {
      hasAutoContinued.current = true;
      onContinue();
    }
  }, [isGateMode, clinicId, alreadyAccepted, onContinue]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_TOLERANCE) {
      setHasScrolledToBottom(true);
    }
  };

  const scrollToBottom = () => {
    scrollAreaRef.current?.scrollTo({
      top: scrollAreaRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const handleCheck = () => {
    if (isChecked) {
      setIsChecked(false);
      return;
    }

    if (!clinicId) {
      // Clinic-creation flow: nothing to persist yet — unlock Continue locally and lift the
      // acceptance up so it can be sent along once the clinic is actually created.
      setIsChecked(true);
      onAcceptLocal?.();
      return;
    }

    acceptAgreement(undefined, {
      onSuccess: () => setIsChecked(true),
    });
  };

  const canContinue = clinicId ? isChecked && !isAccepting : isChecked;

  // Auto-continuing — render nothing while the redundant modal flashes closed.
  if (isGateMode && clinicId && alreadyAccepted) {
    return null;
  }

  return (
    <Modal
      isOpen
      size="large"
      onClose={onBack}
      header={
        <Modal.Title>
          <span className={styles.titleWithIcon}>
            <InfoIcon width="22px" />
            {isGateMode
              ? "Surcharge Enrollment Required"
              : "Surcharge Agreement"}
          </span>
        </Modal.Title>
      }
      footer={
        <div className={styles.footerContainer}>
          {isGateMode ? (
            <>
              <Button variant="outlined" onClick={onBack}>
                Back
              </Button>
              <Button onClick={onContinue} isDisabled={!canContinue}>
                Continue
              </Button>
            </>
          ) : (
            <>
              {clinicId && agreement?.accepted && (
                <Button
                  variant="outlined"
                  onClick={() => downloadAgreement()}
                  isDisabled={isDownloading}
                  leftAdornment={
                    isDownloading ? <CircularProgress size="tiny" /> : null
                  }
                >
                  Download PDF
                </Button>
              )}
              <Button onClick={onBack}>Close</Button>
            </>
          )}
        </div>
      }
      footerNoBorder
    >
      {isLoadingAgreement && clinicId ? (
        <div className={styles.loadingContainer}>
          <CircularProgress size="small" />
        </div>
      ) : (
        <div className={styles.content}>
          {!isGateMode && (
            <div className={styles.acceptanceSummary}>
              {agreement?.accepted ? (
                <p>
                  Accepted by <strong>{agreement.accepted_by?.name}</strong> on{" "}
                  <strong>{agreement.accepted_at}</strong>.
                </p>
              ) : (
                <p>This clinic has not yet accepted the surcharge agreement.</p>
              )}
            </div>
          )}

          <div className={styles.scrollAreaWrapper}>
            <div
              ref={scrollAreaRef}
              className={styles.scrollArea}
              onScroll={handleScroll}
            >
              <SurchargeAgreementArticle />
            </div>
            {!hasScrolledToBottom && (
              <button
                type="button"
                className={styles.scrollDownBtn}
                onClick={scrollToBottom}
                aria-label="Scroll down"
              >
                <AngleDownIcon width="12" height="12" fill="#ffffff" />
              </button>
            )}
          </div>

          {isGateMode && (
            <Checkbox
              label="I have read and accept the Surcharge Agreement"
              isChecked={isChecked}
              isDisabled={(!hasScrolledToBottom && !isChecked) || isAccepting}
              onChange={handleCheck}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default SurchargeAgreementModal;
